import React, { useCallback, useContext, useState } from "react";
import { useDropzone } from "react-dropzone";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSocketListener from "../../hooks/useSocketListener";
import { classifyUploadResponse } from "../../utils/pdfUploadResult";

import {
  MAX_FILES,
  errorReasonLabel,
  isSettled,
  makeClientFileId,
  statusColor,
  statusLabel,
  summarize,
  validateSelection,
} from "./helpers";

/** Quantos uploads simultâneos. O servidor responde 202 na hora (o
 * processamento é da fila), então o gargalo aqui é só a banda de subida. */
const UPLOAD_CONCURRENCY = 3;

const useStyles = makeStyles((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  dropzone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    outline: "none",
    transition: "border-color 0.2s, background-color 0.2s",
  },
  dropzoneActive: {
    borderColor: "#2E7D32",
    backgroundColor: "rgba(46, 125, 50, 0.08)",
  },
  dropzoneIcon: {
    fontSize: 42,
    color: theme.palette.text.secondary,
    opacity: 0.7,
  },
  hint: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  summary: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  progress: {
    flex: 1,
    height: 6,
    borderRadius: 3,
  },
  list: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    marginTop: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: "wrap",
  },
  fileName: {
    flex: 1,
    minWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chip: {
    color: "#FFF",
    fontWeight: "bold",
  },
  reason: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    width: "100%",
  },
  passwordBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
    marginTop: theme.spacing(1),
  },
  rejected: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    borderLeft: "3px solid #C62828",
  },
}));

/**
 * Aba de importação: envio em lote de PDFs de apólice.
 *
 * O upload não espera o processamento: cada arquivo sobe, o backend devolve
 * 202 e enfileira. O progresso chega por Socket.IO
 * (company-{companyId}-policyDocumentImport), correlacionado pelo
 * clientFileId que esta tela gera — nome de arquivo não serve como chave
 * porque pode repetir dentro do mesmo lote.
 *
 * `batchId` vem da página, não daqui: a aba de conferência precisa do mesmo
 * valor para filtrar o lote quando o operador atravessa pelo botão do fim.
 */
const UploadTab = ({ batchId, onGoToConference }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [files, setFiles] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [uploading, setUploading] = useState(false);

  const updateFile = useCallback((clientFileId, patch) => {
    setFiles((previous) =>
      previous.map((file) => (file.clientFileId === clientFileId ? { ...file, ...patch } : file))
    );
  }, []);

  /**
   * Evento de progresso do job. O payload traz só clientFileId, importId,
   * status e errorReason — nome do arquivo fica de fora de propósito (pode
   * conter o nome do segurado), e esta tela já sabe o nome de cada arquivo
   * que ela mesma enviou.
   */
  const onProgress = useCallback(
    (payload) => {
      if (!payload?.clientFileId) return;
      updateFile(payload.clientFileId, {
        importId: payload.importId ?? null,
        status: payload.status,
        errorReason: payload.errorReason ?? null,
      });
    },
    [updateFile]
  );

  useSocketListener(socket, user, "policyDocumentImport", onProgress, [onProgress]);

  const uploadOne = useCallback(
    async (entry, rawFile) => {
      const data = new FormData();
      data.append("file", rawFile);
      data.append("clientFileId", entry.clientFileId);

      try {
        const response = await api.post(`/policy-document-imports?importBatchId=${batchId}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const result = classifyUploadResponse(response?.data, response?.status);
        if (result.kind === "ja_importado") {
          // Recusado: não há job nem evento de socket; não fica em "processando".
          updateFile(entry.clientFileId, { status: "ja_importado", message: result.message, errorReason: null });
          return;
        }
        updateFile(entry.clientFileId, { status: "processando" });
      } catch (err) {
        toastError(err);
        updateFile(entry.clientFileId, { status: "erro_leitura", errorReason: "falha_processamento" });
      }
    },
    [updateFile, batchId]
  );

  const handleDrop = useCallback(
    async (accepted) => {
      const { accepted: valid, rejected: invalid } = validateSelection(accepted, files.length);
      setRejected(invalid);
      if (!valid.length) return;

      const entries = valid.map((rawFile) => ({
        clientFileId: makeClientFileId(),
        name: rawFile.name,
        size: rawFile.size,
        status: "na_fila",
        errorReason: null,
        importId: null,
        passwordDraft: "",
        savingPassword: false,
      }));

      setFiles((previous) => [...previous, ...entries]);
      setUploading(true);

      // Pool pequeno em vez de disparar tudo de uma vez: 50 uploads
      // simultâneos de até 10 MB travariam a aba.
      const queue = entries.map((entry, index) => ({ entry, rawFile: valid[index] }));
      const workers = Array.from({ length: UPLOAD_CONCURRENCY }, async () => {
        while (queue.length) {
          const next = queue.shift();
          if (next) await uploadOne(next.entry, next.rawFile);
        }
      });
      await Promise.all(workers);

      setUploading(false);
    },
    [files.length, uploadOne]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: true,
    disabled: uploading,
  });

  const handleRetryPassword = async (entry) => {
    if (!entry.importId || !entry.passwordDraft.trim()) return;

    updateFile(entry.clientFileId, { savingPassword: true });
    try {
      const { data } = await api.post(`/policy-document-imports/${entry.importId}/retry-password`, {
        password: entry.passwordDraft,
      });
      updateFile(entry.clientFileId, {
        status: data.status,
        errorReason: data.errorReason ?? null,
        passwordDraft: "",
        savingPassword: false,
      });
    } catch (err) {
      toastError(err);
      updateFile(entry.clientFileId, { savingPassword: false });
    }
  };

  const { total, done } = summarize(files);

  return (
    <div className={classes.content}>
        <Paper variant="outlined" {...getRootProps()} className={`${classes.dropzone} ${isDragActive ? classes.dropzoneActive : ""}`}>
          <input {...getInputProps()} />
          <CloudUploadIcon className={classes.dropzoneIcon} />
          <Typography variant="subtitle1">
            {isDragActive ? "Solte os PDFs aqui" : "Arraste os PDFs ou clique para selecionar"}
          </Typography>
          <Typography variant="body2" className={classes.hint}>
            Até {MAX_FILES} arquivos por envio, 10 MB cada. Os arquivos são processados em segundo
            plano — pode acompanhar o progresso abaixo.
          </Typography>
        </Paper>

        {rejected.length > 0 ? (
          <Paper variant="outlined" className={classes.rejected}>
            <Typography variant="body2">
              {rejected.length} arquivo(s) não enviado(s):
            </Typography>
            {rejected.map((item) => (
              <Typography key={item.name} variant="body2" className={classes.reason}>
                {item.name} — {item.reason}
              </Typography>
            ))}
          </Paper>
        ) : null}

        {total > 0 ? (
          <Paper variant="outlined" className={classes.summary}>
            <Typography variant="body2">
              {done} de {total} processado(s)
            </Typography>
            <LinearProgress
              className={classes.progress}
              variant="determinate"
              value={total ? (done / total) * 100 : 0}
            />
            {uploading ? <CircularProgress size={16} /> : null}
            {!uploading && done === total ? (
              <Button size="small" variant="contained" color="primary" onClick={onGoToConference}>
                Conferir documentos importados
              </Button>
            ) : null}
          </Paper>
        ) : null}

        <div className={classes.list}>
          {files.map((entry) => (
            <div key={entry.clientFileId} className={classes.row}>
              <Typography variant="body2" className={classes.fileName} title={entry.name}>
                {entry.name}
              </Typography>

              <Chip
                size="small"
                className={classes.chip}
                style={{ backgroundColor: statusColor(entry.status) }}
                icon={
                  entry.status === "processando" ? (
                    <CircularProgress size={12} style={{ color: "#FFF", marginLeft: 8 }} />
                  ) : undefined
                }
                label={statusLabel(entry.status)}
              />

              {entry.status === "ja_importado" && entry.message ? (
                <Typography variant="body2" className={classes.reason}>
                  {entry.message}
                </Typography>
              ) : null}

              {entry.errorReason && isSettled(entry.status) ? (
                <Typography variant="body2" className={classes.reason}>
                  {errorReasonLabel(entry.errorReason)}
                </Typography>
              ) : null}

              {entry.status === "senha_pendente" && entry.importId ? (
                <div className={classes.passwordBox}>
                  <TextField
                    size="small"
                    variant="outlined"
                    type="password"
                    label="Senha do PDF"
                    value={entry.passwordDraft}
                    disabled={entry.savingPassword}
                    onChange={(event) =>
                      updateFile(entry.clientFileId, { passwordDraft: event.target.value })
                    }
                  />
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={entry.savingPassword || !entry.passwordDraft.trim()}
                    onClick={() => handleRetryPassword(entry)}
                  >
                    {entry.savingPassword ? <CircularProgress size={14} color="inherit" /> : "Reprocessar"}
                  </Button>
                </div>
              ) : null}
            </div>
          ))}
      </div>
    </div>
  );
};

export default UploadTab;
